import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { CandidateStatusColor } from "../Enums/CandidateStatusColor";
import { useAuthToken } from "../State/Hooks/authHooks";
import { useAppSelector } from "../State/Hooks/reduxHooks";
import { selectClientValue } from "../State/Slices/SelectedClientSlice";
import { ICandidateStatusDefinition } from "../Typings/CandidateStatus";
import { apiPathCandidateStatusDefinitions, apiPathCandidates, secureFetcher } from "./queryHelpers";

// See React Query v4 docs for more info on useQuery
// https://tanstack.com/query/v4/docs/quick-start

/**
 * Gets the list of candidate status definitions for the given client.
 * Falls back to the currently selected client when no clientId is provided.
 *
 * @returns UseQueryResult<ICandidateStatusDefinition[]>
 */
export function useCandidateStatusDefinitionsQuery(clientId?: string) {
  const token = useAuthToken();
  const { clientId: selectedClientId } = useAppSelector(selectClientValue);
  const queryClientId = clientId ? clientId : selectedClientId;
  return useQuery<ICandidateStatusDefinition[]>(
    ["candidateStatusDefinitions", queryClientId],
    () => secureFetcher(apiPathCandidateStatusDefinitions + queryClientId, token),
    {
      enabled: !!queryClientId && !!token,
      staleTime: 1000 * 60 * 10, // 10 minutes
    },
  );
}

/**
 * Updates the label and description for a candidate status definition.
 *
 * @returns UseMutationResult<ICandidateStatusDefinition, unknown, { color, label, description }>
 */
export function useUpdateStatusDefinitionMutation() {
  const token = useAuthToken();
  const queryClient = useQueryClient();
  const { clientId } = useAppSelector(selectClientValue);

  return useMutation<
    ICandidateStatusDefinition,
    unknown,
    { color: CandidateStatusColor; label: string; description: string }
  >(
    (variables) => {
      const path = `${apiPathCandidateStatusDefinitions}${clientId}/${variables.color}`;
      return secureFetcher(path, token, {
        method: "PUT",
        body: JSON.stringify({ label: variables.label, description: variables.description }),
        headers: {
          "Content-Type": "application/json",
        },
      });
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["candidateStatusDefinitions", clientId]);
      },
    },
  );
}

/**
 * Sets the status color for a candidate. Pass null to clear the status.
 *
 * @returns UseMutationResult<void, unknown, { candidateId, statusColor }>
 */
export function useSetCandidateStatusMutation() {
  const token = useAuthToken();
  const queryClient = useQueryClient();
  const { clientId } = useAppSelector(selectClientValue);

  return useMutation<void, unknown, { candidateId: string; statusColor: CandidateStatusColor | null }>(
    (variables) => {
      const path = `${apiPathCandidates}${clientId}/${variables.candidateId}/status`;
      return secureFetcher(path, token, {
        method: "PUT",
        body: JSON.stringify({ statusColor: variables.statusColor }),
        headers: {
          "Content-Type": "application/json",
        },
      });
    },
    {
      onSuccess: (_, variables) => {
        queryClient.invalidateQueries(["candidates", clientId]);
        queryClient.invalidateQueries(["candidateProfile", variables.candidateId]);
        queryClient.invalidateQueries(["candidateSearchQuery"]);
      },
    },
  );
}
